import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

export type AssetStatus = 'available' | 'assigned' | 'in_repair' | 'retired'

export interface AssetCategory {
  id: string
  name: string
  description: string
  is_active: boolean
  asset_count: number
  created_at: string
}

export interface AssetListItem {
  id: string
  name: string
  category: string
  category_name: string
  serial_number: string
  purchase_date: string | null
  purchase_cost: string
  status: AssetStatus
  status_display: string
  current_employee_id: string | null
  current_employee_name: string | null
  created_at: string
}

export interface AssetAssignment {
  id: string
  asset: string
  employee: string
  employee_name: string
  assigned_at: string
  returned_at: string | null
  notes: string
  is_active: boolean
  created_at: string
}

export interface AssetDetail extends AssetListItem {
  notes: string
  assignments: AssetAssignment[]
  updated_at: string
}

export interface AssetListResponse {
  assets: AssetListItem[]
  count: number
  page: number
  page_size: number
  pages: number
}

export interface ByEmployeeRow {
  employee_id: string
  employee_name: string
  count: number
  total_cost: string
  assets: { id: string; name: string; category_name: string; purchase_cost: string; assigned_at: string }[]
}

export function useAssetCategories(params?: { active?: 'true' | 'false' }) {
  return useQuery({
    queryKey: ['hr-assets', 'categories', params ?? {}],
    queryFn: async () => {
      const res = await apiClient.get<{ categories: AssetCategory[] }>('/hr-assets/categories/', { params })
      return res.data
    },
  })
}

export function useCreateAssetCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const res = await apiClient.post<AssetCategory>('/hr-assets/categories/', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-assets', 'categories'] }),
  })
}

export function useUpdateAssetCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string; name?: string; description?: string; is_active?: boolean }) => {
      const res = await apiClient.patch<AssetCategory>(`/hr-assets/categories/${id}/`, data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-assets', 'categories'] }),
  })
}

export function useDeleteAssetCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/hr-assets/categories/${id}/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-assets', 'categories'] }),
  })
}

export function useAssets(params: { page?: number; page_size?: number; status?: string; category?: string; employee?: string; search?: string }) {
  return useQuery({
    queryKey: ['hr-assets', 'assets', params],
    queryFn: async () => {
      const res = await apiClient.get<AssetListResponse>('/hr-assets/', { params })
      return res.data
    },
  })
}

export function useAsset(id: string | null) {
  return useQuery({
    queryKey: ['hr-assets', 'asset', id],
    queryFn: async () => {
      const res = await apiClient.get<AssetDetail>(`/hr-assets/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateAsset() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<AssetListItem>) => {
      const res = await apiClient.post<AssetDetail>('/hr-assets/', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-assets'] }),
  })
}

export function useUpdateAsset() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<AssetListItem> & { id: string }) => {
      const res = await apiClient.patch<AssetDetail>(`/hr-assets/${id}/`, data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-assets'] }),
  })
}

export function useDeleteAsset() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/hr-assets/${id}/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-assets'] }),
  })
}

export function useAssignAsset() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, employee_id, assigned_at, notes }: { id: string; employee_id: string; assigned_at: string; notes?: string }) => {
      const res = await apiClient.post<AssetDetail>(`/hr-assets/${id}/assign/`, { employee_id, assigned_at, notes })
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-assets'] }),
  })
}

export function useUnassignAsset() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, returned_at, notes }: { id: string; returned_at: string; notes?: string }) => {
      const res = await apiClient.post<AssetDetail>(`/hr-assets/${id}/unassign/`, { returned_at, notes })
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-assets'] }),
  })
}

export function useAssetsByEmployee() {
  return useQuery({
    queryKey: ['hr-assets', 'by-employee'],
    queryFn: async () => {
      const res = await apiClient.get<{ rows: ByEmployeeRow[]; count: number }>('/hr-assets/by-employee/')
      return res.data
    },
  })
}
